import {
  HomeExpenseDto,
  HomeExpenseGroupDto,
  AccountDto,
  CreateHomeExpenseCommand,
  HomeExpenseFields,
  CreateMultipleHomeExpensesCommand,
  HomeExpenseSummaryDto,
  CategoryUsageDto,
  CategoryMonthStatDto,
  CategoryYearBudgetDto,
  UpsertCategoryYearBudgetsCommand,
  ExpenseType,
} from '../types/homeExpenses';
import { api, handleApiCall, ApiResult } from './api';

// Home Expenses API Service
// Follows the same pattern as other API services using axios and handleApiCall

/**
 * Retrieves all home expenses
 * @param type Optional filter by ExpenseType (Home or Personal)
 */
export const getHomeExpenses = async (type?: ExpenseType): Promise<HomeExpenseDto[]> => {
  const params: Record<string, string> = {};
  if (type !== undefined) params.type = type.toString();
  
  return handleApiCall(() => 
    api.get<ApiResult<HomeExpenseDto[]>>('/api/homeexpenses', {
      params: Object.keys(params).length > 0 ? params : undefined
    })
  );
};

/**
 * Retrieves home expenses within a specific date range
 * @param type Optional filter by ExpenseType (Home or Personal)
 */
const getHomeExpensesByDateRange = async (
  startDate: string, 
  endDate: string,
  type?: ExpenseType
): Promise<HomeExpenseDto[]> => {
  const params: Record<string, string> = { startDate, endDate };
  if (type !== undefined) params.type = type.toString();
  
  return handleApiCall(() => 
    api.get<ApiResult<HomeExpenseDto[]>>('/api/homeexpenses/date-range', {
      params
    })
  );
};

/**
 * Retrieves summary statistics for home expenses
 * Optional date range filtering
 */
export const getHomeExpensesSummary = async (
  startDate?: string, 
  endDate?: string,
  type?: ExpenseType
): Promise<HomeExpenseSummaryDto[]> => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (type !== undefined) params.type = type.toString();
  
  return handleApiCall(() => 
    api.get<ApiResult<HomeExpenseSummaryDto[]>>('/api/homeexpenses/summary', { 
      params: Object.keys(params).length > 0 ? params : undefined 
    })
  );
};

/**
 * Creates a new home expense
 */
export const createHomeExpense = async (command: CreateHomeExpenseCommand): Promise<HomeExpenseDto> => {
  return handleApiCall(() => 
    api.post<ApiResult<HomeExpenseDto>>('/api/homeexpenses', command)
  );
};

/**
 * Creates multiple home expenses in a batch
 */
export const createMultipleHomeExpenses = async (command: CreateMultipleHomeExpensesCommand): Promise<HomeExpenseDto[]> => {
  return handleApiCall(() => 
    api.post<ApiResult<HomeExpenseDto[]>>('/api/homeexpenses/batch', command)
  );
};

/**
 * Updates an existing home expense
 */
export const updateHomeExpense = async (
  publicId: string, 
  command: HomeExpenseFields // an expense keeps its type; the API ignores one sent here
): Promise<HomeExpenseDto> => {
  return handleApiCall(() => 
    api.put<ApiResult<HomeExpenseDto>>(`/api/homeexpenses/${publicId}`, command)
  );
};

/**
 * Deletes a home expense (soft delete)
 */
export const deleteHomeExpense = async (publicId: string): Promise<boolean> => {
  return handleApiCall(() => 
    api.delete<ApiResult<boolean>>(`/api/homeexpenses/${publicId}`)
  );
};

/**
 * Imports home expenses from CSV file into one account (the API refuses an upload without one)
 */
export const importHomeExpensesFromCsv = async (file: File, accountPublicId: string): Promise<HomeExpenseDto[]> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('accountPublicId', accountPublicId);
  
  return handleApiCall(() => 
    api.post<ApiResult<HomeExpenseDto[]>>('/api/homeexpenses/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  );
};

// Home Expense Groups API

/**
 * Retrieves all home expense groups
 */
export const getHomeExpenseGroups = async (type?: ExpenseType): Promise<HomeExpenseGroupDto[]> => {
  const params: Record<string, string> = {};
  if (type !== undefined) params.type = type.toString();
  
  return handleApiCall(() => 
    api.get<ApiResult<HomeExpenseGroupDto[]>>('/api/homeexpenses/groups', {
      params: Object.keys(params).length > 0 ? params : undefined
    })
  );
};

/**
 * Retrieves a specific home expense group by its public ID
 */
export const getHomeExpenseGroupById = async (publicId: string): Promise<HomeExpenseGroupDto> => {
  return handleApiCall(() => 
    api.get<ApiResult<HomeExpenseGroupDto>>(`/api/homeexpenses/groups/${publicId}`)
  );
};

/**
 * Creates a new home expense group
 */
export const createHomeExpenseGroup = async (
  name: string, 
  description?: string, 
  isIncomeGroup: boolean = false,
  color?: string,
  categoryId?: string,
  type?: ExpenseType,
  isShoppingDefault: boolean = false
): Promise<HomeExpenseGroupDto> => {
  return handleApiCall(() => 
    api.post<ApiResult<HomeExpenseGroupDto>>('/api/homeexpenses/groups', { 
      name, 
      description, 
      isIncomeGroup,
      color,
      categoryId,
      type,
      isShoppingDefault
    })
  );
};

/**
 * Updates an existing home expense group
 */
export const updateHomeExpenseGroup = async (
  publicId: string,
  name: string, 
  description?: string, 
  isIncomeGroup: boolean = false,
  color?: string,
  categoryId?: string,
  isShoppingDefault?: boolean
): Promise<HomeExpenseGroupDto> => {
  return handleApiCall(() => 
    api.put<ApiResult<HomeExpenseGroupDto>>(`/api/homeexpenses/groups/${publicId}`, { 
      name, 
      description, 
      isIncomeGroup,
      color,
      categoryId,
      isShoppingDefault
    })
  );
};

/**
 * Deletes a home expense group (soft delete)
 */
export const deleteHomeExpenseGroup = async (publicId: string): Promise<boolean> => {
  return handleApiCall(() => 
    api.delete<ApiResult<boolean>>(`/api/homeexpenses/groups/${publicId}`)
  );
};

// Utility functions for common operations

/**
 * Helper function to format dates for API calls
 */
const formatDateForApi = (date: Date): string => {
  return date.toISOString().split('T')[0]; // YYYY-MM-DD format
};

/**
 * Helper function to get current month's expenses
 */
export const getCurrentMonthExpenses = async (type?: ExpenseType): Promise<HomeExpenseDto[]> => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return getHomeExpensesByDateRange(
    formatDateForApi(startOfMonth),
    formatDateForApi(endOfMonth),
    type
  );
};

/**
 * Helper function to get current year's expenses
 */
export const getCurrentYearExpenses = async (type?: ExpenseType): Promise<HomeExpenseDto[]> => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const endOfYear = new Date(now.getFullYear(), 11, 31);
  
  return getHomeExpensesByDateRange(
    formatDateForApi(startOfYear),
    formatDateForApi(endOfYear),
    type
  );
};

// Account Management API

/**
 * Retrieves all accounts
 * @param type Optional filter by ExpenseType (Home or Personal)
 */
export const getAccounts = async (type?: ExpenseType): Promise<AccountDto[]> => {
  const params: Record<string, string> = {};
  if (type !== undefined) params.type = type.toString();
  
  return handleApiCall(() => 
    api.get<ApiResult<AccountDto[]>>('/api/homeexpenses/accounts', {
      params: Object.keys(params).length > 0 ? params : undefined
    })
  );
};

/**
 * Retrieves a specific account by its public ID
 */
export const getAccountById = async (publicId: string): Promise<AccountDto> => {
  return handleApiCall(() => 
    api.get<ApiResult<AccountDto>>(`/api/homeexpenses/accounts/${publicId}`)
  );
};

/**
 * Creates a new account
 */
export const createAccount = async (
  name: string, 
  description?: string, 
  color?: string,
  isPrimary: boolean = false,
  type?: ExpenseType
): Promise<AccountDto> => {
  return handleApiCall(() => 
    api.post<ApiResult<AccountDto>>('/api/homeexpenses/accounts', { 
      name, 
      description, 
      color,
      isPrimary,
      type 
    })
  );
};

/**
 * Updates an existing account
 */
export const updateAccount = async (
  publicId: string,
  name: string, 
  description?: string, 
  color?: string,
  isPrimary: boolean = false
): Promise<AccountDto> => {
  return handleApiCall(() => 
    api.put<ApiResult<AccountDto>>(`/api/homeexpenses/accounts/${publicId}`, { 
      name, 
      description, 
      color,
      isPrimary 
    })
  );
};

/**
 * Deletes an account (soft delete)
 */
export const deleteAccount = async (publicId: string): Promise<boolean> => {
  return handleApiCall(() => 
    api.delete<ApiResult<boolean>>(`/api/homeexpenses/accounts/${publicId}`)
  );
};

/**
 * Retrieves category usage statistics by year for dashboard analysis
 */
export const getCategoryUsageByYear = async (year?: number): Promise<CategoryUsageDto[]> => {
  const params: Record<string, string> = {};
  if (year) params.year = year.toString();
  
  return handleApiCall(() => 
    api.get<ApiResult<CategoryUsageDto[]>>('/api/homeexpenses/category-usage', { 
      params: Object.keys(params).length > 0 ? params : undefined 
    })
  );
};

/**
 * Retrieves category vs month breakdown analytics
 */
export const getCategoryMonthBreakdown = async (year?: number, type?: ExpenseType): Promise<CategoryMonthStatDto[]> => {
  const params: Record<string, string> = {};
  if (year) params.year = year.toString();
  if (type !== undefined) params.type = type.toString();
  return handleApiCall(() =>
    api.get<ApiResult<CategoryMonthStatDto[]>>('/api/homeexpenses/analytics/category-monthly', {
      params: Object.keys(params).length > 0 ? params : undefined,
    })
  );
};

/**
 * Retrieves category-year budgets with previous-year averages for guidance
 */
export const getCategoryYearBudgets = async (year: number, type?: ExpenseType): Promise<CategoryYearBudgetDto[]> => {
  const params: Record<string, string> = { year: year.toString() };
  if (type !== undefined) params.type = type.toString();
  return handleApiCall(() =>
    api.get<ApiResult<CategoryYearBudgetDto[]>>('/api/homeexpenses/budgets', {
      params,
    })
  );
};

/**
 * Upserts category-year budgets
 */
export const upsertCategoryYearBudgets = async (command: UpsertCategoryYearBudgetsCommand): Promise<boolean> => {
  return handleApiCall(() =>
    api.post<ApiResult<boolean>>('/api/homeexpenses/budgets/upsert', command)
  );
};
